import _ from 'underscore';
import { levelAtConstant } from '@bingads-webui-campaign/scope-constants';
import { parseSync } from '@bingads-webui/safe-json';

function processCampaignFilterValues(filterValues) {
  return _.map(filterValues, filterValue => parseInt(parseSync(filterValue).Id, 10));
}

function processAdGroupFilterValues(filterValues, scope) {
  // only return AdGroup ids which are in current Campaign scope
  const processedValues = _.compact(_.map(filterValues, (filterValue) => {
    const parsedFilter = parseSync(filterValue);
    const filterCampaignId = parseInt(parsedFilter.CampaignId, 10);

    // if we are are in campaign scope, remove any out-of-scope ad group filters
    // for account scope, keep all ad group filters
    const scopeCampaignId = parseInt(scope.campaignId, 10);
    if (_.isFinite(scopeCampaignId) && filterCampaignId !== scopeCampaignId) {
      return false;
    }

    return parseInt(parsedFilter.Id, 10);
  }));

  return processedValues.length > 0 ? processedValues : null;
}

function processKeywordFilterValues(filterValues) {
  return _.map(filterValues, filterValue => parseInt(parseSync(filterValue).Id, 10));
}

export function getFilteredEntities(where, scope) {
  // default value
  let levelAtInRequest = scope.levelAt;
  let entityIdsInRequest = _.map(scope.entityIds, i => parseInt(i, 10));
  let filteredAccount;

  // if there is filter of account, campaign, ad group, re-set levelAt, entityIds
  if (where) {
    let filteredCampaign;
    let filteredAdGroup;
    let filteredKeyword;

    if (where.$and) {
      for (let i = where.$and.length - 1; i >= 0; i -= 1) {
        const expression = where.$and[i];

        if (expression.AdGroup) {
          if (scope.levelAt === levelAtConstant.CAMPAIGN) {
            filteredAdGroup =
              _.union(
                filteredAdGroup,
                processAdGroupFilterValues(expression.AdGroup.$contains, scope)
              );
          }
          where.$and.splice(i, 1);
        }
        if (expression.Campaign) {
          if (scope.levelAt === levelAtConstant.ACCOUNT) {
            filteredCampaign =
              _.union(filteredCampaign, processCampaignFilterValues(expression.Campaign.$contains));
          }
          where.$and.splice(i, 1);
        }
        if (expression.Account) {
          if (scope.levelAt === levelAtConstant.CUSTOMER) {
            filteredAccount =
              _.union(filteredAccount, processCampaignFilterValues(expression.Account.$contains));
          }
          where.$and.splice(i, 1);
        }
      }
      filteredAdGroup = filteredAdGroup && filteredAdGroup.length > 0 ? filteredAdGroup : null;
    } else {
      if (where.Keyword) {
        filteredKeyword = processKeywordFilterValues(where.Keyword.$contains, scope);
      }
      if (where.AdGroup &&
        (scope.levelAt === levelAtConstant.CAMPAIGN || scope.levelAt === levelAtConstant.ACCOUNT)) {
        filteredAdGroup = processAdGroupFilterValues(where.AdGroup.$contains, scope);
      }
      if (where.Campaign && scope.levelAt === levelAtConstant.ACCOUNT) {
        filteredCampaign = processCampaignFilterValues(where.Campaign.$contains);
      }
      if (where.Account && scope.levelAt === levelAtConstant.CUSTOMER) {
        filteredAccount = processCampaignFilterValues(where.Account.$contains);
      }
    }

    // filter only supports 1 entity type in the request
    // we should try to pick the entity lowest in the hierarchy because it's the most specific
    if (!_.isEmpty(filteredKeyword)) {
      levelAtInRequest = levelAtConstant.KEYWORD;
      entityIdsInRequest = filteredKeyword;
    } else if (!_.isEmpty(filteredAdGroup)) {
      levelAtInRequest = levelAtConstant.ADGROUP;
      entityIdsInRequest = filteredAdGroup;
    } else if (!_.isEmpty(filteredCampaign)) {
      levelAtInRequest = levelAtConstant.CAMPAIGN;
      entityIdsInRequest = filteredCampaign;
    }
  }

  if (levelAtInRequest === levelAtConstant.CUSTOMER) {
    if (!_.isEmpty(filteredAccount)) {
      entityIdsInRequest = filteredAccount;
    }
    return {
      AccountIds: entityIdsInRequest,
    };
  }

  return {
    LevelAt: levelAtInRequest,
    EntityIds: entityIdsInRequest,
  };
}

/**
 * Parses the options passed to findAll. Gets the entity type and entity ids to filter on and
 * modifies the options to be in the correct format.
 * @param {Object} op - Options given to findAll call.
 * @param {Object} scope - Current page scope.
 * @returns {Object} - Entity type and entity ids to filter on.
 */
export function getFilteredEntitiesAndUpdateOptions(op, scope) {
  const where = _.result(op, 'where');

  // create the request data needed to filter on entity ids
  const filteredEntities = getFilteredEntities(where, scope);

  if (!_.has(where, '$and')) {
    // prevent these properties from being sent in the request
    op.where = _.omit(op.where, 'Keyword', 'AdGroup', 'Campaign', 'Account'); // eslint-disable-line no-param-reassign
  }

  return filteredEntities;
}
